import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import {
  Edit,
  Users,
  MapPin,
  Clock,
  DollarSign,
  TrendingUp,
  Eye,
  MoreHorizontal,
  Copy,
  Archive,
  AlertTriangle
} from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";

export interface Project {
  id: string;
  title: string;
  company: string;
  role: string;
  department: string;
  location: string;
  status: 'active' | 'paused' | 'completed' | 'draft' | 'archived';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  createdAt: Date;
  deadline: Date;
  budget: {
    min: number;
    max: number;
    currency: string;
  };
  team: Array<{
    id: string;
    name: string;
    role: string;
    avatar?: string;
  }>;
  progress: {
    applicants: number;
    interviews: number;
    offers: number;
    hires: number;
  };
  analytics: {
    totalViews: number;
    applicationRate: number;
    qualityScore: number;
  };
  requirements: {
    experience: string;
    skills: string[];
    education: string;
  };
}

interface ProjectCardProps {
  project: Project;
  viewMode?: 'grid' | 'list';
  onEdit?: () => void;
  onView?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
}

export function ProjectCard({ 
  project, 
  viewMode = 'grid',
  onEdit, 
  onView, 
  onDelete, 
  onDuplicate 
}: ProjectCardProps) {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active': 
        return { 
          bg: 'bg-green-50', 
          text: 'text-green-700', 
          border: 'border-green-200',
          badge: 'bg-green-100 text-green-700 border-green-200'
        };
      case 'paused': 
        return { 
          bg: 'bg-yellow-50', 
          text: 'text-yellow-700', 
          border: 'border-yellow-200',
          badge: 'bg-yellow-100 text-yellow-700 border-yellow-200'
        };
      case 'completed': 
        return { 
          bg: 'bg-blue-50', 
          text: 'text-blue-700', 
          border: 'border-blue-200',
          badge: 'bg-blue-100 text-blue-700 border-blue-200'
        };
      case 'draft': 
        return { 
          bg: 'bg-slate-50', 
          text: 'text-slate-700', 
          border: 'border-slate-200',
          badge: 'bg-slate-100 text-slate-700 border-slate-200'
        };
      default: 
        return { 
          bg: 'bg-slate-50', 
          text: 'text-slate-700', 
          border: 'border-slate-200',
          badge: 'bg-slate-100 text-slate-700 border-slate-200'
        };
    }
  };

  const getPriorityConfig = (priority: string) => {
    switch (priority) {
      case 'urgent': 
        return { 
          badge: 'bg-red-100 text-red-700 border-red-200',
          glow: ''
        };
      case 'high': 
        return { 
          badge: 'bg-orange-100 text-orange-700 border-orange-200',
          glow: ''
        };
      case 'medium': 
        return { 
          badge: 'bg-blue-100 text-blue-700 border-blue-200',
          glow: ''
        };
      case 'low': 
        return { 
          badge: 'bg-slate-100 text-slate-700 border-slate-200',
          glow: ''
        };
      default: 
        return { 
          badge: 'bg-slate-100 text-slate-700 border-slate-200',
          glow: ''
        };
    }
  };

  const calculateOverallProgress = () => {
    const { applicants, interviews, offers, hires } = project.progress;
    if (applicants === 0) return 0;
    return Math.round(((interviews + offers * 2 + hires * 4) / (applicants * 4)) * 100);
  };

  const daysUntilDeadline = Math.ceil((project.deadline.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  const isOverdue = daysUntilDeadline < 0;
  const isUrgent = daysUntilDeadline <= 7 && daysUntilDeadline >= 0;

  const statusConfig = getStatusConfig(project.status);
  const priorityConfig = getPriorityConfig(project.priority);

  const formatDeadline = () => {
    if (isOverdue) {
      return `${Math.abs(daysUntilDeadline)} days overdue`;
    } else if (isUrgent) {
      return `${daysUntilDeadline} days left`;
    } else {
      return project.deadline.toLocaleDateString();
    }
  };

  if (viewMode === 'list') {
    return (
      <Card className="group hover:shadow-md transition-all duration-200 bg-white border border-slate-200 rounded-xl overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 flex-1">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-bold text-lg text-slate-900 truncate">
                    {project.title}
                  </h3>
                  <div className="flex gap-2">
                    <Badge className={`${statusConfig.badge} rounded-lg font-medium px-3 py-1`}>
                      {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                    </Badge>
                    <Badge className={`${priorityConfig.badge} rounded-lg font-medium px-3 py-1`}>
                      {project.priority.charAt(0).toUpperCase() + project.priority.slice(1)}
                    </Badge>
                  </div>
                </div>
                <p className="text-sm text-slate-600 font-medium mb-2">{project.company} • {project.department}</p>
                <div className="flex items-center gap-6 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    <span className="font-medium">{project.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span className={`font-medium ${isOverdue ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-slate-600'}`}>
                      {formatDeadline()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span className="font-medium">{project.progress.applicants} applicants</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-sm font-medium text-slate-600">Progress</div>
                <div className="text-lg font-bold text-slate-900">{calculateOverallProgress()}%</div>
              </div>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-slate-100">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-lg border border-slate-200 bg-white shadow-lg">
                  <DropdownMenuItem onClick={onView} className="rounded-md hover:bg-slate-50">
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onEdit} className="rounded-md hover:bg-slate-50">
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Project
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onDuplicate} className="rounded-md hover:bg-slate-50">
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onDelete} className="rounded-md hover:bg-slate-50 text-red-600">
                    <Archive className="h-4 w-4 mr-2" />
                    Archive
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`group hover:shadow-md transition-all duration-200 bg-white border border-slate-200 rounded-xl overflow-hidden cursor-pointer`}>
      <CardHeader className="p-6 pb-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-3">
              <Badge className={`${statusConfig.badge} rounded-lg font-medium px-3 py-1 text-xs`}>
                {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
              </Badge>
              <Badge className={`${priorityConfig.badge} rounded-lg font-medium px-3 py-1 text-xs`}>
                {project.priority.charAt(0).toUpperCase() + project.priority.slice(1)}
              </Badge>
            </div>
            <h3 className="font-bold text-lg text-slate-900 truncate mb-2" onClick={onView}>
              {project.title}
            </h3>
            <p className="text-sm text-slate-600 font-medium">{project.company}</p>
            <p className="text-xs text-slate-500">{project.department} • {project.role}</p>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-slate-100 opacity-0 group-hover:opacity-100 transition-opacity">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-lg border border-slate-200 bg-white shadow-lg">
              <DropdownMenuItem onClick={onView} className="rounded-md hover:bg-slate-50">
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onEdit} className="rounded-md hover:bg-slate-50">
                <Edit className="h-4 w-4 mr-2" />
                Edit Project
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDuplicate} className="rounded-md hover:bg-slate-50">
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDelete} className="rounded-md hover:bg-slate-50 text-red-600">
                <Archive className="h-4 w-4 mr-2" />
                Archive
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent className="p-6 pt-0 space-y-4 min-w-0">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium truncate">{project.location}</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 flex-shrink-0" />
            <span className={`font-medium ${isOverdue ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-slate-600'}`}>
              {formatDeadline()}
            </span>
            {(isOverdue || isUrgent) && (
              <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
            )}
          </div>
          
          <div className="flex items-start gap-2 text-sm text-slate-600 min-w-0">
            <DollarSign className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span className="font-medium break-words min-w-0">
              {project.budget.min > 0 || project.budget.max > 0 ? (
                `${project.budget.currency} ${project.budget.min.toLocaleString()} - ${project.budget.max.toLocaleString()}`
              ) : (
                'Budget not specified'
              )}
            </span>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-slate-700">Overall Progress</span>
            <span className="text-sm font-bold text-slate-900">{calculateOverallProgress()}%</span>
          </div>
          <Progress value={calculateOverallProgress()} className="h-2" />
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <div className="font-bold text-slate-900">{project.progress.applicants}</div>
            <div className="text-slate-600 text-xs">Applicants</div>
          </div>
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <div className="font-bold text-slate-900">{project.progress.interviews}</div>
            <div className="text-slate-600 text-xs">Interviews</div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div className="flex -space-x-2">
            {project.team.slice(0, 3).map((member) => (
              <Avatar key={member.id} className="h-8 w-8 border-2 border-white">
                <AvatarImage src={member.avatar} alt={member.name} />
                <AvatarFallback className="bg-slate-100 text-slate-700 text-xs">
                  {member.name.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
            ))}
            {project.team.length > 3 && (
              <div className="h-8 w-8 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center">
                <span className="text-xs font-medium text-slate-600">+{project.team.length - 3}</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <TrendingUp className="h-3 w-3" />
            <span>{project.analytics.qualityScore}/10</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
} 